import cn from 'classnames';
import React, { MutableRefObject, PropsWithChildren, useState } from 'react';

type Props = PropsWithChildren<{
  clickable?: boolean;
  href?: string;
  type?: LabelType;
  closable?: boolean;
  onDismiss?: () => void;
  onClick?: (
    event:
      | React.MouseEvent<HTMLSpanElement>
      | React.KeyboardEvent<HTMLSpanElement>
  ) => void;
}>;

type LabelType =
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'purple'
  | 'blue'
  | 'orange'
  | 'light-blue';

const Label = React.forwardRef<HTMLSpanElement | HTMLAnchorElement, Props>(
  (props, ref) => {
    const {
      href,
      children,
      clickable,
      closable,
      onClick,
      onDismiss,
      type
    } = props;
    const [dismissed, setDismissed] = useState(false);
    const classNames = cn('label', {
      clickable,
      [`label-${type}`]: type
    });

    const onLabelClick = (
      event:
        | React.MouseEvent<HTMLSpanElement>
        | React.KeyboardEvent<HTMLSpanElement>
    ) => {
      if (closable) {
        setDismissed(true);
        if (onDismiss) {
          onDismiss();
        }
      }
      if (onClick) {
        onClick(event);
      }
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLSpanElement>) =>
      (e.keyCode === 13 || e.keyCode === 32) && onLabelClick(e);

    return !dismissed ? (
      clickable ? (
        <a
          ref={ref as MutableRefObject<HTMLAnchorElement>}
          href={href || 'javascript://'}
          className={classNames}
          onClick={onLabelClick}
          role="link"
          tabIndex={0}
          onKeyUp={handleKeyUp}>
          {children}
        </a>
      ) : (
        <span ref={ref} className={classNames}>
          {children}
        </span>
      )
    ) : null;
  }
);

export default Label;
